/**
 * `/einstellungen` — die Einstellungen der Buchhaltung. **Nur admin.**
 *
 * Diese Seite zeigt und aendert nichts. Das ist kein Versaeumnis, sondern die
 * Entscheidung aus §15.2: **Die DATEV-Zieladressen sind read-only.** Wer sie
 * hier aendern koennte, koennte alle Belege des Hauses umleiten — an eine
 * Adresse seiner Wahl, mit einem Klick, ohne dass es jemandem auffiele. Sie
 * kommen aus `~/apps/buchhaltung/.env` auf dem Server; wer sie aendern will,
 * aendert sie dort und startet den Stack neu.
 *
 * Dasselbe gilt fuer den Modus (trocken/echt): Der Schritt von „Probemail an
 * Henri" zu „echte Mail an die Kanzlei" ist eine bewusste Entscheidung nach
 * vier Wochen Schattenbetrieb, kein Schalter in einer Oberflaeche.
 *
 * „Einstellungen" ist hier die Einstellung der **Buchhaltung**. Was am
 * einzelnen Menschen haengt — Profil, Benachrichtigungen — liegt auf
 * konto.elbklar.com, erreichbar ueber den eigenen Namen unten links.
 */

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Download, FileSpreadsheet, Lock } from 'lucide-react';

import { bh, fehlerText } from '@/lib/api';
import { datum, datumZeit, euro } from '@/lib/format';
import { useAuth } from '@/features/auth/AuthContext';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { Seitenkopf } from '@/components/ui/Feld';
import { useToast } from '@/components/ui/Toast';
import { Band, Fehler, Laden } from '@/components/ui/Zustaende';
import type { Uebergabeliste } from '@/types';

export function EinstellungenPage() {
  const { user } = useAuth();
  const istAdmin = user?.role === 'admin';

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['einstellungen'],
    queryFn: () => bh.einstellungen(),
    enabled: istAdmin,
  });

  if (!istAdmin) {
    return (
      <div>
        <Seitenkopf titel="Einstellungen" />
        <Band farbe="warnung">
          Diese Seite ist Admins vorbehalten. Die persönlichen Einstellungen — Profil und
          Benachrichtigungen — liegen unter „Konto“, unten links über den eigenen Namen.
        </Band>
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      <Seitenkopf
        titel="Einstellungen"
        beschreibung="Wie die Buchhaltung eingerichtet ist — zum Nachsehen, nicht zum Ändern. Unten die beiden Ausgänge: Übergabeliste und Dokument-Export."
      />

      <div className="mb-5">
        <Band>
          <span className="inline-flex items-center gap-1.5 font-medium text-slate-800">
            <Lock size={13} />
            Alle Einstellungen auf dieser Seite sind read-only.
          </span>{' '}
          Geändert wird in <code className="rounded bg-slate-100 px-1">~/apps/buchhaltung/.env</code>{' '}
          auf dem Server, danach ein Deploy. Wer die DATEV-Adressen in einer Oberfläche ändern
          könnte, könnte alle Belege des Hauses umleiten.
        </Band>
      </div>

      {isLoading && <Laden />}
      {isError && <Fehler text={fehlerText(error)} onErneut={() => void refetch()} />}

      {data && (
        <div className="space-y-5">
          <Block titel="Übergabe an die Kanzlei">
            <Zeile name="Modus">
              <Chip
                klassen={
                  data.datev.modus === 'echt'
                    ? 'border-success-200 bg-success-50 text-success-700'
                    : 'border-warning-200 bg-warning-50 text-warning-700'
                }
              >
                {data.datev.modus === 'echt' ? 'echt' : 'Trockenmodus'}
              </Chip>
              <span className="ml-2 text-xs text-slate-500">
                {data.datev.modus === 'echt'
                  ? 'Belege gehen an die DATEV-Upload-Adressen.'
                  : 'Belege gehen als Probe an Henri, nicht an die Kanzlei.'}
              </span>
            </Zeile>
            <Zeile name="Upload Eingang">{data.datev.upload_eingang ?? '—'}</Zeile>
            <Zeile name="Upload Ausgang">{data.datev.upload_ausgang ?? '—'}</Zeile>
            <Zeile name="Probe-Empfänger">{data.datev.probe_empfaenger ?? '—'}</Zeile>
            <Zeile name="Kanzlei-Domain">{data.datev.kanzlei_domain ?? '—'}</Zeile>
          </Block>

          <Block titel="Postfächer">
            <Zeile name="Sammelpostfach">{data.postfaecher.sammelpostfach}</Zeile>
            <Zeile name="Prüfliste liest">
              {data.postfaecher.sweeper_postfaecher.length > 0
                ? data.postfaecher.sweeper_postfaecher.join(', ')
                : '—'}
            </Zeile>
            <Zeile name="Lese-Zugang">
              {/* Nie den Schluessel selbst, nur ob einer da ist — so steht es
                  im Schema und so bleibt es. */}
              {data.postfaecher.lese_service_account_gesetzt ? (
                <Chip klassen="border-success-200 bg-success-50 text-success-700">hinterlegt</Chip>
              ) : (
                <Chip klassen="border-danger-200 bg-danger-50 text-danger-700">fehlt</Chip>
              )}
              <span className="ml-2 text-xs text-slate-500">
                eigener Service Account, nur Lesen
              </span>
            </Zeile>
          </Block>

          <Block titel="Bank (EBICS)">
            <Zeile name="Eingerichtet">
              {data.ebics.eingerichtet ? (
                <Chip klassen="border-success-200 bg-success-50 text-success-700">ja</Chip>
              ) : (
                <Chip klassen="border-warning-200 bg-warning-50 text-warning-700">
                  noch nicht
                </Chip>
              )}
            </Zeile>
            <Zeile name="Host / Partner / Nutzer">
              {[data.ebics.host_id, data.ebics.partner_id, data.ebics.user_id]
                .filter(Boolean)
                .join(' · ') || '—'}
            </Zeile>
            <Zeile name="Lizenz">
              {data.ebics.lizenz_gesetzt ? (
                <Chip klassen="border-success-200 bg-success-50 text-success-700">gesetzt</Chip>
              ) : (
                <Chip klassen="border-danger-200 bg-danger-50 text-danger-700">fehlt</Chip>
              )}
              <span className="ml-2 text-xs text-slate-500">
                ohne Lizenz meldet der Abruf „keine neuen Umsätze" — ein stiller Ausfall
              </span>
            </Zeile>
            <Zeile name="Letzter Abruf">{datumZeit(data.ebics.letzter_abruf_at)}</Zeile>
            <Zeile name="Jüngster Auszug">{datum(data.ebics.letzter_auszug_am)}</Zeile>
          </Block>

          <Block titel="Belegeingang">
            <Zeile name="Zahlende">
              {data.zahlende_user_ids.length > 0 ? data.zahlende_user_ids.join(', ') : '—'}
            </Zeile>
            <Zeile name="Barbeleg ohne Lieferantenprüfung">
              bis {data.barbeleg_schwelle_eur} €
            </Zeile>
            <Zeile name="Auslesen">
              {data.auslesen_eingerichtet ? 'eingerichtet' : 'nicht eingerichtet'}
              <span className="ml-2 text-xs text-slate-500">
                heute {data.auslese_heute} von höchstens {data.max_auslese_je_tag}
              </span>
            </Zeile>
          </Block>

          <Export />
        </div>
      )}
    </div>
  );
}

/**
 * Übergabeliste und Dokument-Export.
 *
 * Zwei Ausgänge mit zwei Zwecken. Die **Übergabeliste** ist eine CSV je Monat
 * und geht an die Kanzlei zum Abgleich: Sie ersetzt die Handarbeit aus Bauplan
 * §11 — statt zu hoffen, dass alle Belege ankamen, wird es abgeglichen
 * (§15.2). Der **Dokument-Export** ist ein ZIP je Zeitraum für den
 * Prüfungsfall; er ist `admin`, weil dabei der gesamte Belegbestand eines
 * Monats das Haus verlässt.
 *
 * Die CSV entsteht hier im Browser aus der Antwort — der Server liefert die
 * Zeilen als JSON (`Uebergabeliste`), keine fertige Datei. Das ist die
 * bessere Aufteilung: Die Liste ist auch auf dem Bildschirm zu gebrauchen,
 * und wer sie doch als Datei braucht, bekommt sie mit einem Klick.
 */
function Export() {
  const toast = useToast();
  const [zeitraum, setZeitraum] = useState(() => new Date().toISOString().slice(0, 7));
  const [liste, setListe] = useState<Uebergabeliste | null>(null);

  const holen = useMutation({
    mutationFn: () => bh.uebergabeliste(zeitraum),
    onSuccess: (antwort) => {
      setListe(antwort);
      if (antwort.anzahl === 0) toast.hinweis('In diesem Monat wurde nichts übergeben.');
    },
    onError: (fehler) => toast.fehler(fehlerText(fehler)),
  });

  const csvLaden = () => {
    if (!liste) return;
    const kopf = [
      'Archivname',
      'Lieferant',
      'Rechnungsnummer',
      'Rechnungsdatum',
      'Brutto',
      'Übergeben am',
      'Modus',
      'Message-ID',
    ];
    const zeilen = liste.zeilen.map((z) => [
      z.archive_name ?? '',
      z.supplier_name ?? '',
      z.invoice_number ?? '',
      z.invoice_date ? datum(z.invoice_date) : '',
      z.amount_gross ?? '',
      z.uebergeben_at ? datumZeit(z.uebergeben_at) : '',
      z.mode,
      z.message_id ?? '',
    ]);

    // Semikolon und BOM, weil diese Datei in Excel geöffnet wird und nicht in
    // einem Editor. Ohne BOM zerlegt Excel die Umlaute.
    const inhalt =
      '﻿' +
      [kopf, ...zeilen]
        .map((reihe) => reihe.map((feld) => `"${String(feld).replace(/"/g, '""')}"`).join(';'))
        .join('\r\n');

    const url = URL.createObjectURL(new Blob([inhalt], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `uebergabeliste-${liste.zeitraum}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Block titel="Export">
      <Zeile name="Zeitraum">
        <input
          type="month"
          value={zeitraum}
          onChange={(e) => {
            setZeitraum(e.target.value);
            setListe(null);
          }}
          className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
      </Zeile>

      <Zeile name="Übergabeliste">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => holen.mutate()}
            loading={holen.isPending}
          >
            <FileSpreadsheet size={14} className="mr-1.5" />
            Liste holen
          </Button>
          {liste && (
            <Button variant="outline" size="sm" onClick={csvLaden} disabled={liste.anzahl === 0}>
              <Download size={14} className="mr-1.5" />
              Als CSV speichern
            </Button>
          )}
          {liste && (
            <span className="text-xs text-slate-500">
              {liste.anzahl} {liste.anzahl === 1 ? 'Beleg' : 'Belege'} ·{' '}
              {euro(liste.summe_brutto)} im {liste.zeitraum}
            </span>
          )}
        </div>
      </Zeile>

      <Zeile name="Dokument-Export">
        <div className="flex flex-wrap items-center gap-2">
          {/* Kein Blob-Umweg: Ein ZIP eines ganzen Monats gehört nicht in den
              Speicher des Browsers. Der Browser lädt es selbst, das
              Sitzungs-Cookie geht mit, weil same-origin. */}
          <a
            href={bh.dokumentExportUrl(zeitraum)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 transition-colors hover:bg-slate-50"
          >
            <Download size={14} />
            ZIP für {zeitraum} laden
          </a>
          <span className="text-xs text-slate-500">
            Alle Belege des Zeitraums mit Index-CSV — für den Prüfungsfall.
          </span>
        </div>
      </Zeile>

      {liste && liste.anzahl > 0 && (
        <Zeile name="Übergeben">
          <div className="w-full overflow-x-auto">
            <table className="w-full min-w-[36rem] text-left text-xs">
              <thead className="text-slate-500">
                <tr>
                  <th className="py-1 pr-3 font-medium">Archivname</th>
                  <th className="py-1 pr-3 font-medium">Lieferant</th>
                  <th className="py-1 pr-3 font-medium">Nummer</th>
                  <th className="py-1 pr-3 font-medium">Brutto</th>
                  <th className="py-1 font-medium">Übergeben</th>
                </tr>
              </thead>
              <tbody className="text-slate-800">
                {liste.zeilen.map((z) => (
                  <tr key={z.document_id} className="border-t border-slate-100">
                    <td className="py-1.5 pr-3 font-mono">{z.archive_name ?? '—'}</td>
                    <td className="py-1.5 pr-3">{z.supplier_name ?? '—'}</td>
                    <td className="py-1.5 pr-3">{z.invoice_number ?? '—'}</td>
                    <td className="py-1.5 pr-3">{euro(z.amount_gross)}</td>
                    <td className="py-1.5">
                      {datumZeit(z.uebergeben_at)}
                      {z.mode === 'trocken' && (
                        <span className="ml-1.5 text-slate-400">(Probe)</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Zeile>
      )}
    </Block>
  );
}

function Block({ titel, children }: { titel: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white">
      <h2 className="border-b border-slate-100 px-5 py-3 text-sm font-semibold text-slate-900">
        {titel}
      </h2>
      <dl className="divide-y divide-slate-50">{children}</dl>
    </section>
  );
}

function Zeile({ name, children }: { name: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[minmax(0,12rem)_minmax(0,1fr)] gap-4 px-5 py-3 text-sm">
      <dt className="text-slate-500">{name}</dt>
      <dd className="flex flex-wrap items-center break-words text-slate-900">{children}</dd>
    </div>
  );
}
